import React, { Fragment, useEffect, useState } from "react";

import { Info } from "lucide-react";

import { Checkbox, Switch, TextInput } from "../shared/Inputs";
import { Tooltip } from "../shared/Tooltip";
import { ISelectOption } from "../shared/Selects";

import { EEntitlementSource, EProduct } from "../../types/misc.enum";
import { IEntitlementsData, IAvailableEntitlementsData } from "../../types/misc.interface";

const MAX_ASSESSMENTS_CAP = 999999999;
const ALLOCATED_TO_CHILDREN_TOOLTIP = 'It has been allocated to the beneficiaries and cannot be deselected. First un-allocate it from the beneficiaries.';

const hasAllRequiredItemsByKey = <T extends { key: string | number }>(
    required: T[],
    selected: { key: string | number }[],
): boolean => required.every(item => selected.some(s => s.key === item.key));

const hasAllRequiredItemsById = <T extends { id: string }>(
    required: T[],
    selected: { id: string }[],
): boolean => required.every(item => selected.some(s => s.id === item.id));

const hasOnlyAllowedItemsByKey = <T extends { key: string | number }>(
    allowed: T[],
    selected: { key: string | number }[],
): boolean => selected.every(item => allowed.some(a => a.key === item.key));

const hasOnlyAllowedItemsById = <T extends { id: string }>(
    allowed: T[],
    selected: { id: string }[],
): boolean => selected.every(item => allowed.some(a => a.id === item.id));

export const areEntitlementsEqual = (current: IEntitlementsData | null, original: IEntitlementsData | null | undefined): boolean => {
    if (!current) return false;

    const baseline = original ?? {
        products: [],
        mbts_modules: [],
        mcsa_modules: [],
        mbts_assessments: 0,
        mcsa_assessments: 0,
    };

    const isProductsSame =
        current.products.length === baseline.products.length &&
        current.products.every(product => baseline.products.some(p => p.key === product.key));
    const isMbtsModulesSame =
        current.mbts_modules.length === baseline.mbts_modules.length &&
        current.mbts_modules.every(module => baseline.mbts_modules.some(m => m.id === module.id));
    const isMcsaModulesSame =
        current.mcsa_modules.length === baseline.mcsa_modules.length &&
        current.mcsa_modules.every(module => baseline.mcsa_modules.some(m => m.id === module.id));

    return (
        isProductsSame &&
        isMbtsModulesSame &&
        isMcsaModulesSame &&
        current.mbts_assessments === baseline.mbts_assessments &&
        current.mcsa_assessments === baseline.mcsa_assessments
    );
};

export const isEntitlementsInvalid = (entitlements: IEntitlementsData | null, availableEntitlements: IAvailableEntitlementsData | null): boolean => {
    if (!entitlements || !availableEntitlements) return false;

    const { minimum_caps, maximum_caps } = availableEntitlements;

    return (
        !hasAllRequiredItemsByKey(minimum_caps.products, entitlements.products) ||
        !hasOnlyAllowedItemsByKey(maximum_caps.products, entitlements.products) ||
        !hasAllRequiredItemsById(minimum_caps.mbts_modules, entitlements.mbts_modules) ||
        !hasOnlyAllowedItemsById(maximum_caps.mbts_modules, entitlements.mbts_modules) ||
        !hasAllRequiredItemsById(minimum_caps.mcsa_modules, entitlements.mcsa_modules) ||
        !hasOnlyAllowedItemsById(maximum_caps.mcsa_modules, entitlements.mcsa_modules) ||
        entitlements.mbts_assessments < minimum_caps.mbts_assessments ||
        entitlements.mbts_assessments > maximum_caps.mbts_assessments ||
        entitlements.mcsa_assessments < minimum_caps.mcsa_assessments ||
        entitlements.mcsa_assessments > maximum_caps.mcsa_assessments
    );
};

interface IModalEntitlementFormProps {
    isModalOpen: boolean;
    source: EEntitlementSource;
    entitlements: IEntitlementsData | null;
    availableEntitlements: IAvailableEntitlementsData;
    onEntitlementsChange: (entitlements: IEntitlementsData) => void;
    isProcessing: boolean;
    assessmentsTooltip: string;
    isDivider?: boolean;
    isTitle?: boolean;
};
export const ModalEntitlementForm = (props: IModalEntitlementFormProps) => {
    const {
        isModalOpen,
        source,
        entitlements,
        availableEntitlements,
        onEntitlementsChange,
        isProcessing,
        assessmentsTooltip,
        isDivider = true,
        isTitle = true,
    } = props;

    const [selectedProducts, setSelectedProducts] = useState<ISelectOption[]>([]);
    const [selectedMbtsModules, setSelectedMbtsModules] = useState<ISelectOption[]>([]);
    const [selectedMcsaModules, setSelectedMcsaModules] = useState<ISelectOption[]>([]);
    const [mbtsAssessments, setMbtsAssessments] = useState<number>(0);
    const [mbtsAssessmentsError, setMbtsAssessmentsError] = useState<boolean>(false);
    const [mcsaAssessments, setMcsaAssessments] = useState<number>(0);
    const [mcsaAssessmentsError, setMcsaAssessmentsError] = useState<boolean>(false);

    const buildEntitlements = (): IEntitlementsData => ({
        products: selectedProducts.map(product => ({ key: product.value as EProduct, title: product.label })),
        mbts_modules: selectedMbtsModules.map(module => ({ id: module.value as string, title: module.label })),
        mcsa_modules: selectedMcsaModules.map(module => ({ id: module.value as string, title: module.label })),
        mbts_assessments: mbtsAssessments,
        mcsa_assessments: mcsaAssessments,
    });

    const resetForm = () => {
        setSelectedProducts([]);
        setSelectedMbtsModules([]);
        setSelectedMcsaModules([]);
        setMbtsAssessments(0);
        setMbtsAssessmentsError(false);
        setMcsaAssessments(0);
        setMcsaAssessmentsError(false);
    };

    useEffect(() => {
        if (isModalOpen) {
            resetForm();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    const ensureMinOptionsByKey = (selected: ISelectOption[], minimum: { key: string; title: string }[]): ISelectOption[] => {
        const merged = [...selected];
        for (const item of minimum) {
            if (!merged.some(option => option.value === item.key)) {
                merged.push({ value: item.key, label: item.title });
            };
        };
        return merged;
    };

    const ensureMinOptionsById = (selected: ISelectOption[], minimum: { id: string; title: string }[]): ISelectOption[] => {
        const merged = [...selected];
        for (const item of minimum) {
            if (!merged.some(option => option.value === item.id)) {
                merged.push({ value: item.id, label: item.title });
            };
        };
        return merged;
    };

    useEffect(() => {
        if (entitlements && availableEntitlements) {
            setSelectedProducts(ensureMinOptionsByKey(
                entitlements.products?.map(product => ({ value: product.key, label: product.title })) ?? [],
                availableEntitlements.minimum_caps.products ?? [],
            ));
            setSelectedMbtsModules(ensureMinOptionsById(
                entitlements.mbts_modules?.map(module => ({ value: module.id, label: module.title })) ?? [],
                availableEntitlements.minimum_caps.mbts_modules ?? [],
            ));
            setSelectedMcsaModules(ensureMinOptionsById(
                entitlements.mcsa_modules?.map(module => ({ value: module.id, label: module.title })) ?? [],
                availableEntitlements.minimum_caps.mcsa_modules ?? [],
            ));
            setMbtsAssessments(entitlements.mbts_assessments);
            setMbtsAssessmentsError(entitlements.mbts_assessments < availableEntitlements.minimum_caps.mbts_assessments || entitlements.mbts_assessments > availableEntitlements.maximum_caps.mbts_assessments);
            setMcsaAssessments(entitlements.mcsa_assessments);
            setMcsaAssessmentsError(entitlements.mcsa_assessments < availableEntitlements.minimum_caps.mcsa_assessments || entitlements.mcsa_assessments > availableEntitlements.maximum_caps.mcsa_assessments);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entitlements, availableEntitlements]);

    useEffect(() => {
        onEntitlementsChange(buildEntitlements());

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProducts, selectedMbtsModules, selectedMcsaModules, mbtsAssessments, mcsaAssessments]);

    const isMinProduct = (product: EProduct) =>
        availableEntitlements?.minimum_caps?.products?.some(p => p.key === product) ?? false;

    const isMinMbtsModule = (moduleKey: string | null) =>
        !!moduleKey && (availableEntitlements?.minimum_caps?.mbts_modules?.some(m => m.id === moduleKey) ?? false);

    const isMinMcsaModule = (moduleKey: string | null) =>
        !!moduleKey && (availableEntitlements?.minimum_caps?.mcsa_modules?.some(m => m.id === moduleKey) ?? false);

    const handleProductSwitch = (option: ISelectOption, enabled: boolean, product: EProduct) => {
        const isAlreadySelected = selectedProducts.some(p => p.value === option.value);

        if (!enabled && isMinProduct(product)) {
            return;
        };

        if (enabled && !isAlreadySelected) {
            setSelectedProducts(prev => [...prev, option]);
        } else if (!enabled && isAlreadySelected) {
            setSelectedProducts(prev => prev.filter(p => p.value !== option.value));
        };
    };

    const handleMbtsModuleSelection = (option: ISelectOption) => {
        if (selectedProducts.every(product => product.value !== EProduct.MBTS)) {
            return;
        };

        const isAlreadySelected = selectedMbtsModules.some(module => module.value === option.value);
        if (isAlreadySelected) {
            if (isMinMbtsModule(option.value)) {
                return;
            };
            setSelectedMbtsModules(prev => prev.filter(module => module.value !== option.value));
        } else {
            setSelectedMbtsModules(prev => [...prev, option]);
        };
    };

    const handleMcsaModuleSelection = (option: ISelectOption) => {
        if (selectedProducts.every(product => product.value !== EProduct.MCSA)) {
            return;
        };

        const isAlreadySelected = selectedMcsaModules.some(module => module.value === option.value);
        if (isAlreadySelected) {
            if (isMinMcsaModule(option.value)) {
                return;
            };
            setSelectedMcsaModules(prev => prev.filter(module => module.value !== option.value));
        } else {
            setSelectedMcsaModules(prev => [...prev, option]);
        };
    };

    const isFormDisabled = (field: 'products' | 'mbts_modules' | 'mbts_assessments' | 'mcsa_modules' | 'mcsa_assessments') => {
        const commonDisabled = isProcessing || !availableEntitlements;

        if (field === 'products') {
            return commonDisabled || !availableEntitlements?.maximum_caps?.products?.length;
        } else if (field === 'mbts_modules' || field === 'mbts_assessments') {
            return commonDisabled || !availableEntitlements?.maximum_caps?.mbts_modules?.length || selectedProducts.every(product => product.value !== EProduct.MBTS);
        } else if (field === 'mcsa_modules' || field === 'mcsa_assessments') {
            return commonDisabled || !availableEntitlements?.maximum_caps?.mcsa_modules?.length || selectedProducts.every(product => product.value !== EProduct.MCSA);
        };

        return true;
    };

    const isProductSelected = (product: EProduct) => selectedProducts.some(p => p.value === product);

    const getProductOption = (product: EProduct): ISelectOption | null => {
        const match = availableEntitlements?.maximum_caps?.products?.find(p => p.key === product);
        return match ? { value: match.key, label: match.title } : null;
    };

    const mbtsProductOption = getProductOption(EProduct.MBTS);
    const mcsaProductOption = getProductOption(EProduct.MCSA);

    return (
        <Fragment>
            {isDivider && <hr />}
            {isTitle && <p className="text-lg text-medium">Entitlements</p>}
            <div className="modal_entitlement_form mt-xl">
                {mbtsProductOption && availableEntitlements && (
                    <ProductSection
                        source={source}
                        product={EProduct.MBTS}
                        title="STAGE/MBTS"
                        isProductDisabled={isFormDisabled('products')}
                        isProductChecked={isProductSelected(EProduct.MBTS)}
                        isProductLocked={isProductSelected(EProduct.MBTS) && isMinProduct(EProduct.MBTS)}
                        onToggleProduct={enabled => handleProductSwitch(mbtsProductOption, enabled, EProduct.MBTS)}
                        isModulesDisabled={isFormDisabled('mbts_modules')}
                        selectedModules={selectedMbtsModules}
                        onModuleToggle={handleMbtsModuleSelection}
                        assessments={mbtsAssessments}
                        isAssessmentsDisabled={isFormDisabled('mbts_assessments')}
                        onAssessmentsChange={setMbtsAssessments}
                        assessmentsTooltip={assessmentsTooltip.replace('{PRODUCT_NAME}', 'STAGE')}
                        assessmentsError={mbtsAssessmentsError}
                        setAssessmentsError={(error: boolean) => setMbtsAssessmentsError(error)}
                        availableEntitlements={{
                            min_modules: availableEntitlements.minimum_caps?.mbts_modules?.map(module => ({ value: module.id, label: module.title })) ?? [],
                            max_modules: availableEntitlements.maximum_caps?.mbts_modules?.map(module => ({ value: module.id, label: module.title })) ?? [],
                            min_assessments: availableEntitlements.minimum_caps.mbts_assessments,
                            max_assessments: availableEntitlements.maximum_caps.mbts_assessments,
                        }}
                    />
                )}
                {mcsaProductOption && availableEntitlements && (
                    <ProductSection
                        source={source}
                        product={EProduct.MCSA}
                        title="ATLAS/MCSA"
                        isProductDisabled={isFormDisabled('products')}
                        isProductChecked={isProductSelected(EProduct.MCSA)}
                        isProductLocked={isProductSelected(EProduct.MCSA) && isMinProduct(EProduct.MCSA)}
                        onToggleProduct={enabled => handleProductSwitch(mcsaProductOption, enabled, EProduct.MCSA)}
                        isModulesDisabled={isFormDisabled('mcsa_modules')}
                        selectedModules={selectedMcsaModules}
                        onModuleToggle={handleMcsaModuleSelection}
                        assessments={mcsaAssessments}
                        isAssessmentsDisabled={isFormDisabled('mcsa_assessments')}
                        onAssessmentsChange={setMcsaAssessments}
                        assessmentsTooltip={assessmentsTooltip.replace('{PRODUCT_NAME}', 'ATLAS')}
                        assessmentsError={mcsaAssessmentsError}
                        setAssessmentsError={(error: boolean) => setMcsaAssessmentsError(error)}
                        availableEntitlements={{
                            min_modules: availableEntitlements.minimum_caps?.mcsa_modules?.map(module => ({ value: module.id, label: module.title })) ?? [],
                            max_modules: availableEntitlements.maximum_caps?.mcsa_modules?.map(module => ({ value: module.id, label: module.title })) ?? [],
                            min_assessments: availableEntitlements.minimum_caps.mcsa_assessments,
                            max_assessments: availableEntitlements.maximum_caps.mcsa_assessments,
                        }}
                    />
                )}
            </div>
        </Fragment>
    );
};

interface IProductSectionProps {
    source: EEntitlementSource;
    product: EProduct;
    title: string;
    isProductDisabled: boolean;
    isProductChecked: boolean;
    isProductLocked: boolean;
    onToggleProduct: (enabled: boolean) => void;
    isModulesDisabled: boolean;
    selectedModules: ISelectOption[];
    onModuleToggle: (option: ISelectOption) => void;
    isAssessmentsDisabled: boolean;
    assessments: number;
    onAssessmentsChange: (value: number) => void;
    assessmentsTooltip: string;
    assessmentsError: boolean;
    setAssessmentsError: (error: boolean) => void;
    availableEntitlements: {
        min_modules: ISelectOption[];
        max_modules: ISelectOption[];
        min_assessments: number;
        max_assessments: number;
    };
};
const ProductSection = (props: IProductSectionProps) => {
    const {
        source,
        title,
        isProductChecked,
        isProductLocked,
        onToggleProduct,
        isProductDisabled,
        selectedModules,
        onModuleToggle,
        isModulesDisabled,
        assessments,
        onAssessmentsChange,
        isAssessmentsDisabled,
        assessmentsTooltip,
        availableEntitlements,
        assessmentsError,
        setAssessmentsError,
    } = props;

    const handleAssessmentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const min = availableEntitlements.min_assessments;
        const max = availableEntitlements.max_assessments;

        const numericValue = Number(value.replace(/[^0-9]/g, ''));

        onAssessmentsChange(numericValue);
        setAssessmentsError(numericValue < min || numericValue > max);
    };

    const getModulesAvailabilityMessage = () => {
        if (source === EEntitlementSource.CLIENT) {
            return 'No modules available for this product. Please create modules first.';
        } else if (source === EEntitlementSource.SUB_CLIENT) {
            return 'No modules available for this product. Please setup the modules for client first. Those will be reflected here.';
        } else if (source === EEntitlementSource.COHORT) {
            return 'No modules available for this product. Please setup the modules for client first. Those will be reflected here.';
        } else {
            return 'No modules available for this product.';
        };
    };

    const getAreAssessmentsAvailable = () => {
        return availableEntitlements.min_assessments >= 0 && availableEntitlements.max_assessments > 0;
    };

    const getAssessmentsAvailabilityMessage = () => {
        if (source === EEntitlementSource.CLIENT) {
            return 'No assessments available for this product. Please contact MindShield Support.';
        } else if (source === EEntitlementSource.SUB_CLIENT) {
            return 'No assessments available for this product. Please setup the assessments for client first. Those will be reflected here.';
        } else if (source === EEntitlementSource.COHORT) {
            return 'No assessments available for this product. Please setup the assessments for client first. Those will be reflected here.';
        } else {
            return 'No assessments available for this product.';
        };
    };

    return (
        <div className="modal_entitlement_form__product-section">
            <div className="modal_entitlement_form__product-header">
                <p className="text-sm text-medium color--txt-secondary">{title}</p>
                <div className="modal_entitlement_form__product-switch">
                    {isProductLocked && (
                        <Tooltip content={ALLOCATED_TO_CHILDREN_TOOLTIP} wide>
                            <Info className="tooltip__icon" />
                        </Tooltip>
                    )}
                    <Switch
                        checked={isProductChecked}
                        disabled={isProductDisabled || isProductLocked}
                        onChange={onToggleProduct}
                    />
                </div>
            </div>
            {isProductChecked && (
                <div className="modal_entitlement_form__product-body">
                    <div>
                        <p className="text-sm text-medium color--txt-secondary modal_entitlement_form__field-label">Modules</p>
                        {!availableEntitlements.max_modules.length && (
                            <p className="text-sm color--txt-muted">
                                {getModulesAvailabilityMessage()}
                            </p>
                        )}
                        {!!availableEntitlements.max_modules.length && (
                            <ModuleCheckboxGrid
                                modules={availableEntitlements.max_modules}
                                minModules={availableEntitlements.min_modules}
                                selectedModules={selectedModules}
                                onModuleToggle={onModuleToggle}
                                disabled={isModulesDisabled}
                            />
                        )}
                    </div>
                    <div className="modal_entitlement_form__assessment-field">
                        <div className="modal_entitlement_form__field-label">
                            <p className="text-sm text-medium color--txt-secondary">Assessments</p>
                            <Tooltip content={assessmentsTooltip} wide>
                                <Info className="tooltip__icon" />
                            </Tooltip>
                        </div>
                        {!getAreAssessmentsAvailable() && (
                            <p className="text-xs color--txt-muted">
                                {getAssessmentsAvailabilityMessage()}
                            </p>
                        )}
                        <TextInput
                            disabled={isAssessmentsDisabled || !getAreAssessmentsAvailable()}
                            placeholder="Assessments"
                            value={assessments}
                            onChange={handleAssessmentsChange}
                            error={assessmentsError ? 'Invalid number of assessments.' : undefined}
                        />
                        <div className="mt-xs modal_entitlement_form__assessment-field--caps">
                            <div className="modal_entitlement_form__assessment-field--cap">
                                <p className="text-xs text-medium color--txt-muted">
                                    Minimum: {availableEntitlements.min_assessments}
                                </p>
                                <Tooltip content="The minimum number of assessments that must be allocated." wide>
                                    <Info className="tooltip__icon" />
                                </Tooltip>
                            </div>
                            <div className="modal_entitlement_form__assessment-field--cap">
                                <p className="text-xs text-medium color--txt-muted">
                                    Maximum: {availableEntitlements.max_assessments >= MAX_ASSESSMENTS_CAP ? '∞' : availableEntitlements.max_assessments}
                                </p>
                                <Tooltip content="The maximum number of assessments that can be allocated." wide>
                                    <Info className="tooltip__icon" />
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

interface IModuleCheckboxGridProps {
    modules: ISelectOption[];
    minModules: ISelectOption[];
    selectedModules: ISelectOption[];
    onModuleToggle: (option: ISelectOption) => void;
    disabled: boolean;
};
const ModuleCheckboxGrid = (props: IModuleCheckboxGridProps) => {
    const { modules, minModules, selectedModules, onModuleToggle, disabled } = props;

    return (
        <div className="modal_entitlement_form__modules-grid">
            {modules.map(module => {
                const isChecked = selectedModules.some(selected => selected.value === module.value);
                const isLocked = isChecked && minModules.some(minModule => minModule.value === module.value);

                return (
                    <div key={String(module.value)} className="modal_entitlement_form__module-item">
                        <Checkbox
                            checked={isChecked}
                            label={module.label}
                            size="sm"
                            disabled={disabled || isLocked}
                            onChange={() => onModuleToggle(module)}
                        />
                        {isLocked && (
                            <Tooltip content={ALLOCATED_TO_CHILDREN_TOOLTIP} wide>
                                <Info className="tooltip__icon" />
                            </Tooltip>
                        )}
                    </div>
                );
            })}
        </div>
    );
};